import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Link2, Unlink, Search, Star, X, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getProfileGroupSuggestions, upsertProfileGroup, dissolveProfileGroup, unlinkProfileFromGroup } from '../../../lib/api';

const isAdminRole = (u) => ['admin', 'admin_primary', 'admin_secondary'].includes(u?.role);
const shortWallet = (w) => (w ? `${w.slice(0, 6)}…${w.slice(-4)}` : 'nessun wallet');

const UserLine = ({ u }) => (
  <div className="min-w-0 flex-1">
    <p className="text-sm text-white truncate">{u.name}</p>
    <p className="text-[11px] text-disabled truncate">{u.email} · <span className="font-mono">{shortWallet(u.trustwallet_address)}</span></p>
  </div>
);

export const ProfileGroupModal = ({ open, onOpenChange, targetUser, allUsers = [], onChanged }) => {
  const [selected, setSelected] = useState([]);
  const [primaryId, setPrimaryId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type:'unlink'|'dissolve', member}
  const [newPrimaryId, setNewPrimaryId] = useState('');

  const groupId = targetUser?.profile_group_id || null;
  const byId = useMemo(() => Object.fromEntries(allUsers.map(u => [u.id, u])), [allUsers]);
  const members = useMemo(
    () => (groupId ? allUsers.filter(u => u.profile_group_id === groupId).sort((a, b) => (a.profile_index || 99) - (b.profile_index || 99)) : []),
    [allUsers, groupId]
  );

  useEffect(() => {
    if (!open || !targetUser) return;
    const initial = members.length ? members.map(m => m.id) : [targetUser.id];
    setSelected(initial);
    setPrimaryId(members.find(m => m.profile_index === 1)?.id || targetUser.id);
    setSearch('');
    getProfileGroupSuggestions(targetUser.id)
      .then(res => setSuggestions(res.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [open, targetUser, members]);

  const selectedUsers = selected.map(id => byId[id]).filter(Boolean);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter(u => !isAdminRole(u) && !selected.includes(u.id))
      .filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, allUsers, selected]);
  const visibleSuggestions = suggestions.filter(s => !selected.includes(s.user_id));
  const inOtherGroup = (u) => u?.profile_group_id && u.profile_group_id !== groupId;

  const add = (id) => setSelected(prev => (prev.includes(id) ? prev : [...prev, id]));
  const remove = (id) => {
    setSelected(prev => prev.filter(x => x !== id));
    if (primaryId === id) setPrimaryId(null);
  };

  const handleSave = async () => {
    if (selected.length < 2) return toast.error('Seleziona almeno 2 profili');
    if (!primaryId || !selected.includes(primaryId)) return toast.error('Indica il profilo principale');
    setSaving(true);
    try {
      await upsertProfileGroup(selected, primaryId, groupId);
      toast.success(groupId ? 'Gruppo aggiornato' : 'Profili collegati');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      if (confirm.type === 'dissolve') {
        await dissolveProfileGroup(groupId);
        toast.success('Gruppo sciolto: nessun profilo è più collegato');
      } else {
        const isPrimary = confirm.member.profile_index === 1;
        const res = await unlinkProfileFromGroup(groupId, confirm.member.id, isPrimary ? newPrimaryId || null : null);
        toast.success(res.data.dissolved ? 'Gruppo sciolto (restava un solo profilo)' : `${confirm.member.name} scollegato`);
      }
      setConfirm(null);
      setNewPrimaryId('');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setSaving(false);
    }
  };

  if (!targetUser) return null;
  const remainingAfterUnlink = confirm?.type === 'unlink' ? members.filter(m => m.id !== confirm.member.id) : [];
  const needsNewPrimary = confirm?.type === 'unlink' && confirm.member.profile_index === 1 && remainingAfterUnlink.length >= 2;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#111827] border-[#282c34] text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="profile-group-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 size={18} className="ht-pos" /> Profili collegati</DialogTitle>
          </DialogHeader>

          {members.length > 0 && (
            <div className="bg-[#11161d]/40 rounded-lg p-3 space-y-2" data-testid="profile-group-current">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-disabled">Gruppo attuale</p>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400 hover:bg-red-500/10" data-testid="dissolve-group-btn"
                  onClick={() => setConfirm({ type: 'dissolve' })}>
                  <Unlink size={12} className="mr-1" /> Sciogli gruppo
                </Button>
              </div>
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-2 py-1" data-testid={`group-member-${m.profile_index}`}>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.profile_index === 1 ? 'border-amber-500/50 ht-warn' : 'border-gray-600 text-gray-300'}`}>
                    Profilo {m.profile_index}
                  </span>
                  <UserLine u={m} />
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400 hover:text-red-400" data-testid={`unlink-member-btn-${m.profile_index}`}
                    onClick={() => { setNewPrimaryId(''); setConfirm({ type: 'unlink', member: m }); }}>
                    Scollega
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-disabled">{groupId ? 'Modifica gruppo' : 'Nuovo collegamento'}</p>
            <div className="space-y-1" data-testid="profile-group-selected">
              {selectedUsers.map(u => (
                <div key={u.id} className={`flex items-center gap-2 rounded-lg p-2 border ${primaryId === u.id ? 'border-amber-500/40 bg-amber-500/5' : 'border-[#282c34] bg-[#0d1117]/40'}`}>
                  <button onClick={() => setPrimaryId(u.id)} title="Imposta come principale" data-testid={`set-primary-btn-${u.id}`}
                    className={`p-1 rounded ${primaryId === u.id ? 'ht-warn' : 'text-[#5f6672] hover:text-[#f0b45a]'}`}>
                    <Star size={14} fill={primaryId === u.id ? 'currentColor' : 'none'} />
                  </button>
                  <UserLine u={u} />
                  {inOtherGroup(u) && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> già in altro gruppo</span>}
                  <button onClick={() => remove(u.id)} className="text-gray-500 hover:text-white" data-testid={`remove-selected-btn-${u.id}`}><X size={14} /></button>
                </div>
              ))}
              <p className="text-[11px] text-disabled">★ = Profilo 1 (principale). Gli altri diventano Profilo 2, 3… nell'ordine elencato.</p>
            </div>

            {visibleSuggestions.length > 0 && (
              <div data-testid="profile-group-suggestions">
                <p className="text-xs text-gray-400 flex items-center gap-1 mb-1"><Sparkles size={12} className="ht-pos" /> Profili suggeriti (stesso nome o wallet)</p>
                <div className="space-y-1">
                  {visibleSuggestions.map(s => (
                    <button key={s.user_id} onClick={() => add(s.user_id)} data-testid={`suggestion-btn-${s.user_id}`}
                      className="w-full flex items-center gap-2 rounded-lg p-2 border border-usdt/20 hover:bg-[rgba(61,219,169,.08)] text-left">
                      <UserLine u={s} />
                      <span className="text-[10px] ht-pos">{s.match}</span>
                      {s.profile_group_id && s.profile_group_id !== groupId && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> altro gruppo</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-disabled" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca utente per nome o email…"
                className="pl-8 ht-panel text-white h-9" data-testid="profile-group-search-input" />
            </div>
            {candidates.length > 0 && (
              <div className="space-y-1" data-testid="profile-group-search-results">
                {candidates.map(u => (
                  <button key={u.id} onClick={() => { add(u.id); setSearch(''); }} data-testid={`search-result-btn-${u.id}`}
                    className="w-full flex items-center gap-2 rounded-lg p-2 border border-[#282c34] hover:bg-[#11161d]/60 text-left">
                    <UserLine u={u} />
                    {inOtherGroup(u) && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> altro gruppo</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#282c34] text-gray-300">Annulla</Button>
            <Button onClick={handleSave} disabled={saving || selected.length < 2} className="ht-btn ht-btn-primary" data-testid="profile-group-save-btn">
              {saving ? 'Salvo…' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="ht-panel" data-testid="profile-group-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirm?.type === 'dissolve' ? 'Sciogliere il gruppo?' : `Scollegare ${confirm?.member?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {confirm?.type === 'dissolve'
                ? 'Tutti i profili verranno scollegati e il cambio profilo sarà disattivato subito per ognuno.'
                : remainingAfterUnlink.length < 2
                  ? 'Resterebbe un solo profilo: il gruppo verrà sciolto completamente.'
                  : 'Il profilo non potrà più passare agli altri e viceversa, con effetto immediato.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {needsNewPrimary && (
            <div className="space-y-1">
              <p className="text-xs ht-warn">Stai scollegando il principale: scegli il nuovo Profilo 1</p>
              <select value={newPrimaryId} onChange={e => setNewPrimaryId(e.target.value)} data-testid="new-primary-select"
                className="w-full bg-[#11161d] border border-[#282c34] rounded-md text-sm text-white p-2">
                <option value="">— seleziona —</option>
                {remainingAfterUnlink.map(m => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
              </select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#11161d] text-white border-[#282c34]">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                if (needsNewPrimary && !newPrimaryId) {
                  e.preventDefault();
                  toast.error('Indica il nuovo profilo principale');
                  return;
                }
                handleConfirm();
              }}
              disabled={saving} className="ht-btn ht-btn-danger" data-testid="profile-group-confirm-btn">
              {saving ? 'Attendi…' : 'Conferma'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProfileGroupModal;
